import BarChart from '@cloudscape-design/components/bar-chart';
import Box from '@cloudscape-design/components/box';
import Spinner from '@cloudscape-design/components/spinner';
import Alert from '@cloudscape-design/components/alert';
import { Car, CarType } from '../api/cars';
import { UseCarsResult } from './useCars';

// Fixed order so the x axis stays stable as cars are added or removed.
const TYPE_ORDER: CarType[] = [
  'REGULAR',
  'SPORT',
  'PERFORMANCE',
  'SUPER',
  'EXOTIC',
  'HYPER',
];

const label = (t: string) => t.charAt(0) + t.slice(1).toLowerCase();

const averageTopSpeedByType = (cars: Car[]) =>
  TYPE_ORDER.map((type) => {
    const inType = cars.filter((c) => c.type === type);
    const avg = inType.length
      ? inType.reduce((sum, c) => sum + c.topSpeed, 0) / inType.length
      : 0;
    return { x: label(type), y: Math.round(avg) };
  });

const countByType = (cars: Car[]) =>
  TYPE_ORDER.map((type) => ({
    x: label(type),
    y: cars.filter((c) => c.type === type).length,
  }));

export const CarChart = ({ cars, loading, error, configured }: UseCarsResult) => {
  if (!configured) {
    return (
      <Alert type="info" header="GraphQL not configured">
        Set REACT_APP_GRAPHQL_URL and REACT_APP_GRAPHQL_API_KEY in my-app/.env
        from the CarStack deploy output, then restart the dev server.
      </Alert>
    );
  }

  if (loading) {
    return (
      <Box textAlign="center" padding={{ vertical: 'xxl' }}>
        <Spinner size="large" />
        <Box variant="p" color="text-status-inactive">
          Loading cars…
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert type="error" header="Could not load cars">
        {error}
      </Alert>
    );
  }

  const speedData = averageTopSpeedByType(cars);
  const maxSpeed = Math.max(100, ...speedData.map((d) => d.y));

  return (
    <BarChart
      series={[
        {
          title: 'Average top speed (mph)',
          type: 'bar',
          data: speedData,
          valueFormatter: (e: number) => `${Math.round(e)} mph`,
        },
        {
          title: 'Cars in collection',
          type: 'bar',
          data: countByType(cars),
          valueFormatter: (e: number) => `${Math.round(e)}`,
        },
      ]}
      xDomain={TYPE_ORDER.map(label)}
      yDomain={[0, Math.ceil((maxSpeed * 1.15) / 10) * 10]}
      ariaLabel="Average top speed and car count by category"
      height={300}
      xTitle="Category"
      yTitle="Average top speed (mph) and count"
      hideFilter
      empty={
        <Box textAlign="center" color="inherit">
          <b>No cars yet</b>
          <Box variant="p" color="inherit">
            Add cars through the GraphQL API and they will appear here.
          </Box>
        </Box>
      }
      noMatch={
        <Box textAlign="center" color="inherit">
          <b>No matching data</b>
        </Box>
      }
    />
  );
};
